import { memoizeGetCssVariableValue, memoizeHexToRgb } from '@/utils/memoizations'
import { THRESHOLDS_DEFAULT_VALUES } from '@/utils/thresholds'

export const MIN_GRADE_VALUE = 0
const MAX_GRADE_VALUE = 100

export const calculateGradeValue = (targetValue, originalValue, currentValue) => {
  const [normalizedTargetValue, normalizedOriginalValue, normalizedCurrentValue] = [
    targetValue,
    originalValue,
    currentValue
  ].map(Number)

  if (normalizedTargetValue === normalizedOriginalValue) {
    // Condition added in scope of https://oboard.atlassian.net/browse/OK-2731
    return normalizedCurrentValue === normalizedTargetValue ? MAX_GRADE_VALUE : MIN_GRADE_VALUE
  }

  if (
    (normalizedOriginalValue < normalizedTargetValue &&
      normalizedCurrentValue < normalizedOriginalValue) ||
    (normalizedOriginalValue > normalizedTargetValue &&
      normalizedCurrentValue > normalizedOriginalValue)
  ) {
    return MIN_GRADE_VALUE
  }

  let result = Math.round(
    ((normalizedCurrentValue - normalizedOriginalValue) /
      (normalizedTargetValue - normalizedOriginalValue)) *
      100
  )

  if (result < MIN_GRADE_VALUE || Number.isNaN(result)) {
    result = MIN_GRADE_VALUE
  }

  return result
}

export const getGradeDisplayValue = grade => {
  return Math.round(grade)
}

export const GRADE_VALUE_DEFAULT_COLOR = '#172B4D'

export const CUSTOM_COLORS = {
  'primary-color-next': '--primary-color-next'
}
export const THRESHOLDS_COLORS = {
  AT_RISK: '--grade-low-color-next',
  BEHIND: '--grade-medium-color-next',
  ON_TRACK: '--grade-high-color-next',
  CLOSED: '--grade-closed-color',
  ABANDONED: '--grade-abandoned-color',
  BACKLOG: '--grade-backlog-color',
  DEFAULT: '--grade-default-color',
  NOT_STARTED: '--grade-default-color'
}

export const GRADE_COLOR_NAMES = {
  GRADE_GREEN: 'grade-green',
  GRADE_PURPUR: 'grade-purpur',
  GRADE_RED: 'grade-red',
  GRADE_DEFAULT: 'grade-default',
  GRADE_WHITE: 'grade-white',
  GRADE_BLACK: 'grade-black',
  GRADE_BLUE: 'grade-blue',
  GRADE_YELLOW: 'grade-yellow',
  GRADE_GRAY: 'grade-gray'
}

const {
  GRADE_GREEN,
  GRADE_PURPUR,
  GRADE_RED,
  GRADE_DEFAULT,
  GRADE_WHITE,
  GRADE_BLACK,
  GRADE_BLUE,
  GRADE_YELLOW,
  GRADE_GRAY
} = GRADE_COLOR_NAMES

export const GRADE_COLORS = {
  [GRADE_GREEN]: {
    variable: THRESHOLDS_COLORS.ON_TRACK
  },
  [GRADE_PURPUR]: {
    variable: THRESHOLDS_COLORS.BEHIND
  },
  [GRADE_RED]: {
    variable: THRESHOLDS_COLORS.AT_RISK
  },
  [GRADE_DEFAULT]: {
    variable: THRESHOLDS_COLORS.DEFAULT
  },
  [GRADE_WHITE]: {
    variable: THRESHOLDS_COLORS.CLOSED
  },
  [GRADE_BLACK]: {
    variable: THRESHOLDS_COLORS.BACKLOG
  },
  [GRADE_BLUE]: {
    variable: THRESHOLDS_COLORS.ABANDONED
  },
  [GRADE_YELLOW]: {
    variable: THRESHOLDS_COLORS.NOT_STARTED
  },
  [GRADE_GRAY]: {
    variable: THRESHOLDS_COLORS.DEFAULT
  }
}

export const getGradeColorByValue = ({
  value = 0,
  thresholdBehind = THRESHOLDS_DEFAULT_VALUES.BEHIND,
  thresholdOnTrack = THRESHOLDS_DEFAULT_VALUES.ON_TRACK
}) => {
  const { AT_RISK, BEHIND, ON_TRACK } = THRESHOLDS_COLORS
  const [color] = [
    value < thresholdBehind && AT_RISK,
    value < thresholdOnTrack && BEHIND,
    ON_TRACK
  ].filter(Boolean)

  return color
}

export const getGradeColorNameByValue = ({
  value = 0,
  thresholdBehind = THRESHOLDS_DEFAULT_VALUES.BEHIND,
  thresholdOnTrack = THRESHOLDS_DEFAULT_VALUES.ON_TRACK
}) => {
  const [name] = [
    value <= thresholdBehind && 'grade-red',
    value <= thresholdOnTrack && 'grade-purpur',
    'grade-green'
  ].filter(Boolean)

  return name
}

export const getGradeColorStyle = gradeColor => {
  const colorVariable = GRADE_COLORS[gradeColor]?.variable || THRESHOLDS_COLORS.AT_RISK

  const hex = memoizeGetCssVariableValue(colorVariable)
  const rgbColor = memoizeHexToRgb(hex.trim())

  return {
    color: `var(${colorVariable})`,
    '--color': `var(${colorVariable})`,
    '--rgb-color': rgbColor
  }
}

export const getGradeColorVariable = gradeColor =>
  GRADE_COLORS[gradeColor]?.variable || CUSTOM_COLORS[gradeColor] || THRESHOLDS_COLORS.AT_RISK

export const showGradeMultiplier = (weight, weightMultiplier) => {
  return weight !== null && weightMultiplier !== null
}
